import React, { useMemo } from 'react';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  AutocompleteControl,
  AutocompleteMenu,
  MenuItem,
  useAutocomplete,
} from '@adsk/alloy-react-dropdown';
import Footer, { ClearButton } from '@adsk/alloy-react-footer';
import PropTypes from 'prop-types';

const CompaniesDropdown = ({
  isLoadingCompanies,
  onCompanyChange,
  companies,
  selectedCompany,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const orderedCompanies = useMemo(() => {
    return selectedCompany
      ? [
          selectedCompany,
          ...companies.filter((company) => company.id !== selectedCompany.id),
        ]
      : companies;
  }, [companies, selectedCompany]);

  const {
    closeMenu,
    controlProps,
    menuProps: allMenuProps,
  } = useAutocomplete({
    id: 'ProjectMemberFilterPanel_Company_Autocomplete',
    items: orderedCompanies,
    itemToString: (item) => item?.name || '',
    onSelectedItemChange: ({ selectedItem: selected }) => {
      onCompanyChange(selected);
    },
    selectedItem: selectedCompany,
  });

  const { menuItems, getItemProps, ...menuProps } = allMenuProps;

  return (
    <>
      <AutocompleteControl
        {...controlProps}
        placeholder={T.translate('COMPANY_PLACEHOLDER')}
      />
      <AutocompleteMenu
        {...menuProps}
        isLoading={isLoadingCompanies}
        renderFooter={() => (
          <Footer>
            <ClearButton
              onClick={() => {
                onCompanyChange(null);
                closeMenu();
              }}
            />
          </Footer>
        )}
        style={{ maxHeight: '200px' }}
      >
        {menuItems.map((item, index) => (
          <MenuItem
            key={index}
            {...getItemProps?.({ item })}
            data-testid={`Dropdown__MenuItem--${index}`}
            text={item.name}
          />
        ))}
      </AutocompleteMenu>
    </>
  );
};

CompaniesDropdown.propTypes = {
  companies: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  isLoadingCompanies: PropTypes.bool.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  selectedCompany: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default CompaniesDropdown;
